import { Flex, Form, Input, Modal, Select, notification } from 'antd';
import { useMemo, useState } from 'react';
import { UpdateRollout } from '../api/go';
import type { UpdateModuleVersionRecord, UpdateRolloutRecord } from '../api/go';
import {
  MODULE_STATUS_ENABLED,
  PercentFields,
  RELEASE_STATUS_ENABLED,
  ROLLOUT_STATUS_DRAFT,
  ROLLOUT_STATUS_OFFICIAL,
  buildRolloutModuleState,
  buildRolloutStatusSelectOptions,
  canEditRolloutPercent,
  getErrorMessage,
  handleRolloutPercentValuesChange,
  isDisabledStatusOption,
  isOfficialRolloutStatus,
  normalizeRolloutSubmitValues,
  officialRolloutPercentValues,
  showCheckJSONPublishWarning,
} from './shared';
import type { RolloutPercentForm } from './shared';

type RolloutEditForm = RolloutPercentForm & {
  remark?: string;
};

type RolloutEditorMode = 'create' | 'edit';

type RolloutEditorOptions = {
  onSaved?: (target: UpdateModuleVersionRecord, rollout: UpdateRolloutRecord) => void | Promise<void>;
};

function moduleRecordFromRollout(row: UpdateRolloutRecord): UpdateModuleVersionRecord {
  return {
    id: row.module_version_id,
    release_id: row.release_id || 0,
    release_name: row.release_name || '',
    release_status: row.release_status || RELEASE_STATUS_ENABLED,
    module_key: row.module_key,
    module_version: row.module_version || '',
    version_key: row.version_key || '',
    build_number: row.build_number || 0,
    url: row.url || '',
    download_url: row.download_url || '',
    sha1: row.sha1 || '',
    status: MODULE_STATUS_ENABLED,
    created_at: '',
    updated_at: '',
  };
}

export function useRolloutEditor(options: RolloutEditorOptions = {}) {
  const [rolloutForm] = Form.useForm<RolloutEditForm>();
  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState<RolloutEditorMode>('create');
  const [editTarget, setEditTarget] = useState<UpdateModuleVersionRecord | null>(null);
  const [editRollout, setEditRollout] = useState<UpdateRolloutRecord | null>(null);
  const [moduleRollouts, setModuleRollouts] = useState<UpdateRolloutRecord[]>([]);
  const [moduleVersionRolloutIDs, setModuleVersionRolloutIDs] = useState<number[]>([]);
  const [rolloutRulesReady, setRolloutRulesReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checkingRollouts, setCheckingRollouts] = useState(false);
  const [firstRolloutConfirm, setFirstRolloutConfirm] = useState(false);

  const downloadPercent = Form.useWatch('download_percent', rolloutForm) ?? 0;
  const rolloutStatus = Form.useWatch('status', rolloutForm);
  const rolloutCanEditPercent = canEditRolloutPercent(rolloutStatus);
  const editCurrentRollout = useMemo(() => {
    if (!editRollout) {
      return undefined;
    }
    return moduleRollouts.find((rollout) => rollout.id === editRollout.id) || editRollout;
  }, [editRollout, moduleRollouts]);
  const editRolloutState = useMemo(
    () => buildRolloutModuleState(moduleRollouts, editCurrentRollout?.id),
    [editCurrentRollout, moduleRollouts],
  );
  const editRolloutStatusOptions = useMemo(
    () => buildRolloutStatusSelectOptions(editCurrentRollout, editRolloutState, rolloutRulesReady),
    [editCurrentRollout, editRolloutState, rolloutRulesReady],
  );
  const rolloutStatusDisabled = isDisabledStatusOption(editRolloutStatusOptions, rolloutStatus);

  function closeRolloutEdit() {
    setEditOpen(false);
    setEditMode('create');
    setEditTarget(null);
    setEditRollout(null);
    setModuleRollouts([]);
    setModuleVersionRolloutIDs([]);
    setRolloutRulesReady(false);
    setCheckingRollouts(false);
    setFirstRolloutConfirm(false);
  }

  async function loadRolloutRules(row: UpdateModuleVersionRecord, mode: RolloutEditorMode) {
    setCheckingRollouts(true);
    try {
      const [moduleRolloutData, moduleVersionRolloutData] = await Promise.all([
        UpdateRollout.list({ module_key: row.module_key }),
        UpdateRollout.list({ module_version_id: row.id }),
      ]);
      const rollouts = moduleRolloutData.records || [];
      setModuleRollouts(rollouts);
      setModuleVersionRolloutIDs((moduleVersionRolloutData.records || []).map((rollout) => rollout.id));
      setRolloutRulesReady(true);

      const isFirstRollout = rollouts.length === 0;
      setFirstRolloutConfirm(mode === 'create' && isFirstRollout);
      if (mode === 'create' && isFirstRollout) {
        rolloutForm.setFieldsValue({
          ...officialRolloutPercentValues(),
          status: ROLLOUT_STATUS_OFFICIAL,
        });
      }
    } catch (err) {
      notification.error({ message: '检查模块 rollout 失败', description: await getErrorMessage(err) });
    } finally {
      setCheckingRollouts(false);
    }
  }

  async function openRolloutCreate(row: UpdateModuleVersionRecord) {
    setEditMode('create');
    setEditTarget(row);
    setEditRollout(null);
    setModuleRollouts([]);
    setModuleVersionRolloutIDs([]);
    setRolloutRulesReady(false);
    setFirstRolloutConfirm(false);
    rolloutForm.setFieldsValue({
      download_percent: 0,
      use_percent: 0,
      status: ROLLOUT_STATUS_DRAFT,
      remark: '',
    });
    setEditOpen(true);
    await loadRolloutRules(row, 'create');
  }

  async function openRolloutEdit(row: UpdateRolloutRecord) {
    const module = moduleRecordFromRollout(row);
    setEditMode('edit');
    setEditTarget(module);
    setEditRollout(row);
    setModuleRollouts([]);
    setModuleVersionRolloutIDs([]);
    setRolloutRulesReady(false);
    setFirstRolloutConfirm(false);
    const percentValues = isOfficialRolloutStatus(row.status)
      ? officialRolloutPercentValues()
      : {
          download_percent: row.download_percent,
          use_percent: row.use_percent,
        };
    rolloutForm.setFieldsValue({
      ...percentValues,
      status: row.status,
      remark: row.remark || '',
    });
    setEditOpen(true);
    await loadRolloutRules(module, 'edit');
  }

  async function saveRollout(values: RolloutEditForm) {
    if (!editTarget) {
      return;
    }

    setSaving(true);
    try {
      const nextValues = normalizeRolloutSubmitValues(values);
      const resp = await UpdateRollout.upsert({
        id: editRollout?.id,
        module_version_id: editTarget.id,
        download_percent: nextValues.download_percent,
        use_percent: nextValues.use_percent,
        status: nextValues.status,
        remark: nextValues.remark?.trim(),
      });
      notification.success({
        message: editMode === 'edit' ? '更新 rollout 成功' : '创建 rollout 成功',
        description: `Rollout #${resp.rollout.id}`,
      });
      showCheckJSONPublishWarning(resp.check_json_publish);
      const savedTarget = editTarget;
      closeRolloutEdit();
      await options.onSaved?.(savedTarget, resp.rollout);
    } catch (err) {
      notification.error({ message: '保存 rollout 失败', description: await getErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  }

  async function submitRollout(values: RolloutEditForm) {
    if (!editTarget) {
      return;
    }
    if (isDisabledStatusOption(editRolloutStatusOptions, values.status)) {
      notification.warning({ message: '当前状态不可用于该模块版本' });
      return;
    }

    if (editMode === 'create' && moduleVersionRolloutIDs.length > 0) {
      Modal.confirm({
        title: '确认继续创建 rollout',
        content: `当前模块版本已创建 ${moduleVersionRolloutIDs.length} 个 rollout，相关 ID：${moduleVersionRolloutIDs.join(', ')}。确认继续创建新的 rollout？`,
        okText: '继续创建',
        cancelText: '取消',
        onOk: () => saveRollout(values),
      });
      return;
    }

    if (editMode === 'create' && firstRolloutConfirm) {
      Modal.confirm({
        title: '确认创建正式版本',
        content: '该模块还没有 rollout，本次创建会直接成为正式版本，确认创建？',
        okText: '确认创建',
        cancelText: '取消',
        onOk: () => saveRollout(values),
      });
      return;
    }

    await saveRollout(values);
  }

  const modalTitle =
    editTarget && editMode === 'edit'
      ? `编辑 rollout #${editRollout?.id}：${editTarget.module_key} / ${editTarget.module_version || '-'}`
      : editTarget
        ? `创建 rollout：${editTarget.module_key} / ${editTarget.module_version || '-'}`
        : '编辑 rollout';

  const modal = (
    <Modal
      title={modalTitle}
      open={editOpen}
      onCancel={closeRolloutEdit}
      onOk={() => rolloutForm.submit()}
      confirmLoading={saving || checkingRollouts}
      okButtonProps={{ disabled: rolloutStatusDisabled }}
      width={720}
      destroyOnHidden
    >
      <Form<RolloutEditForm>
        form={rolloutForm}
        layout="vertical"
        onFinish={submitRollout}
        preserve={false}
        onValuesChange={(changed, values) => handleRolloutPercentValuesChange(rolloutForm, changed, values)}
      >
        {rolloutCanEditPercent ? (
          <PercentFields downloadMax={downloadPercent} disabled={saving || checkingRollouts || rolloutStatusDisabled} />
        ) : null}
        <Flex gap="middle" wrap>
          <Form.Item<RolloutEditForm>
            label="状态"
            name="status"
            rules={[{ required: true, message: '请选择状态' }]}
            style={{ flex: '1 1 180px' }}
          >
            <Select options={editRolloutStatusOptions} disabled={saving || checkingRollouts} />
          </Form.Item>
        </Flex>
        <Form.Item<RolloutEditForm> label="备注" name="remark" rules={[{ max: 512 }]}>
          <Input.TextArea rows={3} maxLength={512} showCount disabled={saving || checkingRollouts || rolloutStatusDisabled} />
        </Form.Item>
      </Form>
    </Modal>
  );

  return {
    modal,
    openRolloutCreate,
    openRolloutEdit,
  };
}
